import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import ToastBody from "@/components/ui/toast-body";
import { useAPIMutation } from "@/services/api/hook";

interface InviteNotetakerRequest {
  meetingUrl: string;
  scheduledJoinAt?: string;
  meetingTitle?: string;
}

interface InviteNotetakerResponse {
  id: string;
  status: string;
  scheduledJoinAt: string;
}

/**
 * Meeting links always carry the meeting identity in the path, so a bare
 * host ("google.com") is rejected. A missing scheme is tolerated by
 * assuming https — users paste links copied from invite bodies.
 */
export const normalizeMeetingUrl = (rawUrl: string): string | null => {
  let candidate = rawUrl.trim();
  if (!candidate) return null;
  if (candidate.startsWith("//")) {
    candidate = `https:${candidate}`;
  } else if (!candidate.includes("://")) {
    candidate = `https://${candidate}`;
  }
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;
  if (!parsed.hostname.includes(".")) return null;
  if (parsed.pathname.replace(/\//g, "").length === 0) return null;
  return candidate;
};

interface InviteNotetakerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Reopens the modal — wired to the error toast's "Try again" action. */
  onRetry: () => void;
  /** Prefills the meeting link input when invoked from a meeting activity. */
  initialMeetingUrl?: string;
  /** Meeting start time (ISO) — the notetaker joins at this time. */
  scheduledJoinAt?: string;
  meetingTitle?: string;
  /** Opens the expanded meeting activity — shown as "View meeting" in the success toast. */
  onViewMeeting?: () => void;
}

const InviteNotetakerModal: React.FC<InviteNotetakerModalProps> = ({
  isOpen,
  onClose,
  onRetry,
  initialMeetingUrl,
  scheduledJoinAt,
  meetingTitle,
  onViewMeeting
}) => {
  const { t } = useTranslation();
  const [meetingUrl, setMeetingUrl] = useState(initialMeetingUrl ?? "");
  const [hasValidationError, setHasValidationError] = useState(false);

  const showSuccessToast = () => {
    toast.success(
      <ToastBody
        title={t("notetaker.toast.success")}
        actions={
          onViewMeeting && (
            <Button
              variant="secondaryNoBg"
              onClick={() => {
                onViewMeeting();
                toast.dismiss();
              }}
            >
              {t("notetaker.toast.viewMeeting")}
            </Button>
          )
        }
      />,
      { duration: 5000 }
    );
  };

  const showErrorToast = () => {
    toast.error(
      <ToastBody
        title={t("notetaker.toast.error")}
        subtitle={t("notetaker.toast.errorSubtitle")}
        actions={
          <Button
            variant="secondaryNoBg"
            onClick={() => {
              onRetry();
              toast.dismiss();
            }}
          >
            {t("notetaker.toast.tryAgain")}
          </Button>
        }
      />,
      { duration: 8000 }
    );
  };

  const inviteNotetaker = useAPIMutation<
    { data: InviteNotetakerRequest },
    InviteNotetakerResponse
  >(
    "inviteNotetaker",
    {},
    () => {
      onClose();
      showSuccessToast();
    },
    () => {
      onClose();
      showErrorToast();
    }
  );

  const handleUrlChange = (value: string) => {
    setMeetingUrl(value);
    if (hasValidationError) {
      setHasValidationError(false);
    }
  };

  const handleAddNotetaker = () => {
    const normalizedUrl = normalizeMeetingUrl(meetingUrl);
    if (!normalizedUrl) {
      setHasValidationError(true);
      return;
    }
    inviteNotetaker.mutate({
      data: {
        meetingUrl: normalizedUrl,
        ...(scheduledJoinAt && { scheduledJoinAt }),
        ...(meetingTitle && { meetingTitle })
      }
    });
  };

  const handleClose = () => {
    setHasValidationError(false);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      setIsOpen={(open) => {
        if (!open) {
          handleClose();
        }
      }}
      contentClassname="w-[35rem]"
      data-test="invite-notetaker-modal"
      title={
        <Text as="div" variant="lgSubtitleH1">
          {t("notetaker.modal.title")}
        </Text>
      }
      content={
        <div className="flex flex-col gap-4 px-6 pt-4 pb-4">
          <div className="flex flex-col gap-1">
            <Text
              as="label"
              variant="bodySecondary"
              htmlFor="invite-notetaker-meeting-url"
            >
              {t("notetaker.modal.meetingLink")}
            </Text>
            <Input
              id="invite-notetaker-meeting-url"
              value={meetingUrl}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder={t("notetaker.modal.placeholder")}
              hasError={hasValidationError}
              data-test="invite-notetaker-url-input"
            />
            {hasValidationError && (
              <Text variant="bodySecondary" className="text-error">
                {t("notetaker.modal.invalidUrl")}
              </Text>
            )}
          </div>
          <Text variant="bodySecondary">{t("notetaker.modal.joinNote")}</Text>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={inviteNotetaker.isPending}
            data-test="invite-notetaker-cancel-button"
          >
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleAddNotetaker}
            disabled={!meetingUrl.trim() || inviteNotetaker.isPending}
            data-test="invite-notetaker-add-button"
          >
            {inviteNotetaker.isPending
              ? t("notetaker.modal.adding")
              : t("notetaker.modal.addNotetaker")}
          </Button>
        </div>
      }
    />
  );
};

export default InviteNotetakerModal;
